import { useMemo } from 'react';

import { searchContext, userAppContext } from '@/context/Context';
import { fetchGdaEvidencesWithTimeline } from '@/services/evidenceTimelineService';
import {
  buildEvidenceTimelineCacheKey,
  useEvidenceTimelineCacheStore,
} from '@/store/evidenceTimelineCacheStore';
import { useEvidenceTimelineStore } from '@/store/evidenceTimelineStore';
import { usePrevalenceBreakdownStore } from '@/store/prevalenceBreakdownStore';
import { useScoreBreakdownStore } from '@/store/scoreBreakdownStore';

import type { SearchContext } from '@/types';

import '@/components/Table/EvidenceTimeline/EvidenceTimeline.css';

type EvidenceTimelineCellButtonProps = {
  rowData: any;
};

type YearRangeState =
  | { kind: 'range'; minYear: number; maxYear: number }
  | { kind: 'noPublications' }
  | { kind: 'missing' };

function getYearRangeState(row: any): YearRangeState {
  const yi = Number(row?.yearInitial);
  const yf = Number(row?.yearFinal);
  if (Number.isFinite(yi) && Number.isFinite(yf)) {
    if (yi === 0 && yf === 0) return { kind: 'noPublications' };
    return { kind: 'range', minYear: yi, maxYear: yf };
  }
  const fr = Number(row?.firstRef);
  const lr = Number(row?.lastRef);
  if (Number.isFinite(fr) && Number.isFinite(lr)) {
    if (fr === 0 && lr === 0) return { kind: 'noPublications' };
    return { kind: 'range', minYear: fr, maxYear: lr };
  }
  return { kind: 'missing' };
}

export default function EvidenceTimelineCellButton({ rowData }: EvidenceTimelineCellButtonProps) {
  const { source, tab } = searchContext() as SearchContext;
  const userCtx = userAppContext() as {
    authorization?: unknown;
    setAuthError?: (e: unknown) => void;
  };
  const authorization = userCtx?.authorization;
  const setAuthError = userCtx?.setAuthError ?? (() => {});

  const setEvidenceTimelineRow = useEvidenceTimelineStore(s => s.setEvidenceTimelineRow);
  const setScoreBreakdownRow = useScoreBreakdownStore(s => s.setScoreBreakdownRow);
  const setPrevalenceBreakdownRow = usePrevalenceBreakdownStore(s => s.setPrevalenceBreakdownRow);

  const setCacheEntry = useEvidenceTimelineCacheStore(s => s.setCacheEntry);

  const cacheKey = useMemo(
    () => buildEvidenceTimelineCacheKey({ ...rowData, source }),
    [
      rowData?.assocID,
      rowData?.geneSymbol,
      rowData?.variantStrID,
      rowData?.strID,
      rowData?.diseaseName,
      source,
    ],
  );

  const cached = useEvidenceTimelineCacheStore(s => s.cache[cacheKey]);

  const yearState = useMemo(() => getYearRangeState(rowData), [rowData]);

  const summaryYears = yearState.kind === 'range' ? yearState : null;
  const disabled = yearState.kind !== 'range';
  const displayMin = summaryYears?.minYear;
  const displayMax = summaryYears?.maxYear;

  const cellLabel =
    yearState.kind === 'noPublications'
      ? 'No publications'
      : yearState.kind === 'missing'
        ? '-'
        : `${displayMin}-${displayMax}`;

  const barHeight = 30;
  const dotSize = 7;
  const dotTop = barHeight / 2;

  const isNoPublications = yearState.kind === 'noPublications';

  return (
    <button
      type="button"
      disabled={disabled}
      className={`evidence-timeline-cell-button${isNoPublications ? ' evidence-timeline-cell-button--no-publications' : ''}`}
      onClick={e => {
        e.stopPropagation();
        if (disabled) return;
        setScoreBreakdownRow(null);
        setPrevalenceBreakdownRow(null);

        const hasCachedTimeline = Array.isArray(cached?.timeline) && cached.timeline.length > 0;

        const baseMerged = {
          ...rowData,
          ...(hasCachedTimeline
            ? { timeline: cached.timeline, publicationsByYear: cached?.publicationsByYear ?? undefined }
            : {}),
        };

        const variantName = String(rowData?.variantStrID ?? rowData?.strID ?? '');
        const diseaseFilter = String(rowData?.diseaseUMLSCUI ?? '').replace(/^UMLS_/, '');
        const canFetchTimeline =
          Boolean(source) &&
          Boolean(diseaseFilter) &&
          (tab === 'GDA' ? Boolean(rowData?.geneSymbol) : tab === 'VDA' ? Boolean(variantName) : false);

        setEvidenceTimelineRow(baseMerged);

        if (hasCachedTimeline) {
          return;
        }

        if (!canFetchTimeline) {
          return;
        }

        setEvidenceTimelineRow({
          ...baseMerged,
          _evidenceTimelineLoading: true,
          _evidenceTimelineError: false,
        });

        fetchGdaEvidencesWithTimeline({
          source: source as string,
          diseaseFilter,
          page_number: 0,
          page_size: 50,
          authorization,
          setAuthError,
          tab: tab.toLowerCase() as string,
          ...(tab === 'VDA' ? { variantName } : { geneSymbol: String(rowData.geneSymbol) }),
        })
          .then(result => {
            setCacheEntry(cacheKey, result);

            const open = useEvidenceTimelineStore.getState().evidenceTimelineRow;
            const openKey = open ? buildEvidenceTimelineCacheKey({ ...open, source }) : null;
            if (openKey !== cacheKey) {
              return;
            }

            setEvidenceTimelineRow({
              ...rowData,
              timeline: result.timeline ?? undefined,
              publicationsByYear: result.publicationsByYear ?? undefined,
              _evidenceTimelineLoading: false,
              _evidenceTimelineError: false,
            });
          })
          .catch(() => {
            const open = useEvidenceTimelineStore.getState().evidenceTimelineRow;
            const openKey = open ? buildEvidenceTimelineCacheKey({ ...open, source }) : null;
            if (openKey !== cacheKey) return;

            setEvidenceTimelineRow({
              ...baseMerged,
              _evidenceTimelineLoading: false,
              _evidenceTimelineError: true,
            });
          });
      }}
      title={
        yearState.kind === 'noPublications'
          ? 'No publications'
          : disabled
            ? 'No evidence timeline'
            : `Years ${displayMin}-${displayMax}`
      }
      aria-label={
        yearState.kind === 'noPublications'
          ? 'No publications'
          : yearState.kind === 'missing'
            ? 'No evidence timeline'
            : 'Open evidence timeline'
      }
      style={{
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 18,
          height: barHeight,
          marginLeft: 2,
          borderRadius: 9999,
          background: 'rgba(37,99,235,0.06)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: 4,
            bottom: 4,
            width: 2,
            transform: 'translateX(-50%)',
            background: 'linear-gradient(to bottom, rgba(96,165,250,0.9), rgba(37,99,235,0.9))',
            opacity: 0.6,
          }}
        />
        {!disabled && (
          <div
            style={{
              position: 'absolute',
              left: '50%',
              top: dotTop,
              width: dotSize,
              height: dotSize,
              transform: 'translate(-50%, -50%)',
              borderRadius: 9999,
              background: '#2563eb',
              boxShadow: '0 0 0 3px rgba(37,99,235,0.18)',
            }}
          />
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div className="evidence-timeline-cell-button__year">{cellLabel}</div>
      </div>
    </button>
  );
}
